import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Button } from "react-bootstrap";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  IconButton,
} from "@mui/material";
import { PiSortDescendingLight, PiSortAscendingLight } from "react-icons/pi";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { FaArrowLeft } from "react-icons/fa";
import authService from "../../../utils/authService";

interface BatchProject {
  batch_id: string;
  batch_name: string;
  slots_given: number;
  slots_taken: number;
}

interface SlotData {
  course_name: string;
  batch_project: BatchProject[];
}

interface SlotsResponse {
  slots: SlotData[];
}

const ProjectSlotsListing: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { project_name } = useParams<{ project_name: string }>();
  const [slots, setSlots] = useState<SlotData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<keyof BatchProject | "course_name" | "slots_remaining">("course_name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Get track_id from navigation state
  const trackId = (location.state as any)?.track_id || "";

  useEffect(() => {
    fetchSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSlots = async () => {
    try {
      setLoading(true);
      // Get project_id from URL query parameter
      const projectId = new URLSearchParams(window.location.search).get("id");
      
      if (!projectId) {
        console.error("Project ID not found in URL");
        setLoading(false);
        setSlots([]);
        return;
      }
      
      const response = await authService.get(`admin/slots_list/${projectId}/`);
      const data: SlotsResponse = response.data;
      setSlots(data.slots || []);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching slots:", error);
      setLoading(false);
      setSlots([]);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field as keyof BatchProject | "course_name");
      setSortDirection("asc");
    }
  };

  const handleBack = () => {
    navigate("/admin/project", {
      state: {
        track_id: trackId,
      },
    });
  };

  const getSortedSlots = (): SlotData[] => {
    const filtered = slots.filter((slot) =>
      slot.course_name.toLowerCase().includes(search.toLowerCase()) ||
      slot.batch_project.some((bp) =>
        bp.batch_name.toLowerCase().includes(search.toLowerCase())
      )
    );

    if (!sortField) {
      return filtered;
    }

    return [...filtered].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      if (sortField === "course_name") {
        aValue = a.course_name.toLowerCase();
        bValue = b.course_name.toLowerCase();
      } else {
        // For batch_project fields, we'll sort by the first batch_project item
        // or aggregate values
        const aFirst = a.batch_project[0];
        const bFirst = b.batch_project[0];
        
        if (sortField === "batch_name") {
          aValue = aFirst?.batch_name?.toLowerCase() || "";
          bValue = bFirst?.batch_name?.toLowerCase() || "";
        } else if (sortField === "slots_given") {
          aValue = a.batch_project.reduce((sum, bp) => sum + bp.slots_given, 0);
          bValue = b.batch_project.reduce((sum, bp) => sum + bp.slots_given, 0);
        } else if (sortField === "slots_taken") {
          aValue = a.batch_project.reduce((sum, bp) => sum + bp.slots_taken, 0);
          bValue = b.batch_project.reduce((sum, bp) => sum + bp.slots_taken, 0);
        } else if (sortField === "slots_remaining") {
          aValue = a.batch_project.reduce((sum, bp) => sum + (bp.slots_given - bp.slots_taken), 0);
          bValue = b.batch_project.reduce((sum, bp) => sum + (bp.slots_given - bp.slots_taken), 0);
        } else {
          return 0;
        }
      }

      // Numeric sort
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      // String sort
      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return 0;
    });
  };

  const sortedSlots = getSortedSlots();

  // Flatten slots for table display
  const flattenedData: Array<{
    course_name: string;
    batch_id: string;
    batch_name: string;
    slots_given: number;
    slots_taken: number;
    slots_remaining: number;
  }> = [];

  sortedSlots.forEach((slot) => {
    slot.batch_project.forEach((bp) => {
      flattenedData.push({
        course_name: slot.course_name,
        batch_id: bp.batch_id,
        batch_name: bp.batch_name,
        slots_given: bp.slots_given,
        slots_taken: bp.slots_taken,
        slots_remaining: bp.slots_given - bp.slots_taken,
      });
    });
  });

  return (
    <div
      className="border rounded-2 bg-white my-2 me-2"
      style={{
        height: `calc(100vh - 90px)`,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div className="p-2" style={{ flexGrow: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <TableContainer
          component={Paper}
          sx={{ 
            flexGrow: 1,
            overflowY: "auto",
            overflowX: "auto",
            borderRadius: 0,
            "& .MuiTableHead-root": {
              position: "sticky",
              top: 0,
              zIndex: 10,
              "& th": {
                backgroundColor: "#604262",
                color: "#fff",
              },
            },
          }}
        >
        <Table 
          className="border border-2" 
          size="small" 
          stickyHeader
        >
          <TableHead>
            <TableRow>
              <TableCell className="muifont mui-header-cell">SL No</TableCell>
              <TableCell
                className="muifont mui-header-cell mui-sortable-header"
                onClick={() => handleSort("course_name")}
                style={{ cursor: "pointer" }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <span>Course Name</span>
                  <span style={{ marginLeft: "4px" }}>
                    {sortField === "course_name" ? (
                      sortDirection === "asc" ? (
                        <PiSortDescendingLight size={16} />
                      ) : (
                        <PiSortAscendingLight size={16} />
                      )
                    ) : (
                      <HiOutlineDotsVertical size={16} />
                    )}
                  </span>
                </Box>
              </TableCell>
              <TableCell
                className="muifont mui-header-cell mui-sortable-header"
                onClick={() => handleSort("batch_name")}
                style={{ cursor: "pointer" }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <span>Batch Name</span>
                  <span style={{ marginLeft: "4px" }}>
                    {sortField === "batch_name" ? (
                      sortDirection === "asc" ? (
                        <PiSortDescendingLight size={16} />
                      ) : (
                        <PiSortAscendingLight size={16} />
                      )
                    ) : (
                      <HiOutlineDotsVertical size={16} />
                    )}
                  </span>
                </Box>
              </TableCell>
              <TableCell
                className="muifont mui-header-cell mui-sortable-header"
                onClick={() => handleSort("slots_given")}
                style={{ cursor: "pointer" }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <span>Slots Given</span>
                  <span style={{ marginLeft: "4px" }}>
                    {sortField === "slots_given" ? (
                      sortDirection === "asc" ? (
                        <PiSortDescendingLight size={16} />
                      ) : (
                        <PiSortAscendingLight size={16} />
                      )
                    ) : (
                      <HiOutlineDotsVertical size={16} />
                    )}
                  </span>
                </Box>
              </TableCell>
              <TableCell
                className="muifont mui-header-cell mui-sortable-header"
                onClick={() => handleSort("slots_taken")}
                style={{ cursor: "pointer" }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <span>Slots Taken</span>
                  <span style={{ marginLeft: "4px" }}>
                    {sortField === "slots_taken" ? (
                      sortDirection === "asc" ? (
                        <PiSortDescendingLight size={16} />
                      ) : (
                        <PiSortAscendingLight size={16} />
                      )
                    ) : (
                      <HiOutlineDotsVertical size={16} />
                    )}
                  </span>
                </Box>
              </TableCell>
              <TableCell
                className="muifont mui-header-cell mui-sortable-header"
                onClick={() => handleSort("slots_remaining")}
                style={{ cursor: "pointer" }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <span>Slots Remaining</span>
                  <span style={{ marginLeft: "4px" }}>
                    {sortField === "slots_remaining" ? (
                      sortDirection === "asc" ? (
                        <PiSortDescendingLight size={16} />
                      ) : (
                        <PiSortAscendingLight size={16} />
                      )
                    ) : (
                      <HiOutlineDotsVertical size={16} />
                    )}
                  </span>
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  align="center"
                  style={{ padding: "20px", color: "#666" }}
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : flattenedData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  align="center"
                  style={{ padding: "20px", color: "#666" }}
                >
                  No slots found
                </TableCell>
              </TableRow>
            ) : (
              flattenedData.map((row, index) => (
                <TableRow
                  key={`${row.course_name}-${row.batch_id}-${index}`}
                  style={{
                    backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
                  }}
                  sx={{
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                >
                  <TableCell className="muifont mui-body-cell">{index + 1}</TableCell>
                  <TableCell className="muifont mui-body-cell">
                    {row.course_name || ""}
                  </TableCell>
                  <TableCell className="muifont mui-body-cell">
                    {row.batch_name || ""}
                  </TableCell>
                  <TableCell className="muifont mui-body-cell">
                    {row.slots_given || 0}
                  </TableCell>
                  <TableCell className="muifont mui-body-cell">
                    {row.slots_taken || 0}
                  </TableCell>
                  <TableCell className="muifont mui-body-cell">
                    {row.slots_remaining || 0}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </div>
    </div>
  );
};

export default ProjectSlotsListing;

